import React, { useState, useMemo, useEffect } from "react";
import {
  BookOpen,
  Search,
  Filter,
  Layout,
  PlayCircle,
  CheckCircle2,
  Users,
  ExternalLink,
  Edit3,
  Clock,
  Layers,
  ShieldCheck,
  Check
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import { useToast } from "../../hooks/otherHooks/useToast";
import {
  fetchAssignedCourses,
  fetchAssignedTracks,
  CourseData,
  TrackData
} from "../../services/curriculumService";
import { useInstructorCurriculumQuery } from "../../hooks/queries/useCurriculumAdminQueries";

interface UnifiedCurriculumItem {
  id: string | number;
  title: string;
  slug: string;
  type: "course" | "track";
  status: string;
  level: string;
  duration: string;
  online_price: number | string;
  onsite_price: number | string;
  thumbnail: string;
  modulesCount: number;
  lessonsCount: number;
  tasksCount: number;
  studentsCount: string | number;
  coursesInTrack?: number;
  raw: CourseData | TrackData;
}

const GlassCard = ({ children, className = "" }: { children: React.ReactNode; className?: string }) => (
  <div className={`cht-bg-white/90 cht-backdrop-blur-xl cht-border cht-border-gray-100 cht-shadow-[0_20px_50px_rgba(0,0,0,0.04)] ${className}`}>
    {children}
  </div>
);

const CourseCard = ({
  item,
  onEdit,
  onPreview
}: {
  item: UnifiedCurriculumItem;
  onEdit: (item: UnifiedCurriculumItem) => void;
  onPreview: (item: UnifiedCurriculumItem) => void;
}) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95 }}
    className="cht-group"
  >
    <GlassCard className="cht-rounded-[28px] cht-overflow-hidden cht-transition-all cht-duration-300 hover:cht-shadow-[0_24px_50px_rgba(62,87,218,0.08)] hover:-cht-translate-y-1">
      {/* Thumbnail & Badges */}
      <div className="cht-relative cht-aspect-[16/10] cht-overflow-hidden cht-bg-slate-100">
        <img
          src={item.thumbnail || "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80"}
          alt={item.title}
          className="cht-w-full cht-h-full cht-object-cover cht-transition-transform cht-duration-500 group-hover:cht-scale-105"
          onError={(e: any) => {
            e.target.src = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop&q=80";
          }}
        />
        <div className="cht-absolute cht-inset-0 cht-bg-gradient-to-t cht-from-black/75 cht-via-black/20 cht-to-transparent" />

        {/* Top Badges */}
        <div className="cht-absolute cht-top-3.5 cht-left-3.5 cht-flex cht-items-center cht-gap-2">
          <span className={`cht-px-3 cht-py-1 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-widest cht-backdrop-blur-md ${item.type === 'track'
              ? 'cht-bg-[#3e57da]/90 cht-text-white'
              : 'cht-bg-indigo-900/90 cht-text-white'
            }`}>
            {item.type === 'track' ? 'Career Track' : 'Standard Course'}
          </span>
          {item.level && (
            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[9px] cht-font-bold cht-bg-white/80 cht-text-slate-800 cht-backdrop-blur-md">
              {item.level}
            </span>
          )}
        </div>

        {/* Top Right Action: Approval Status */}
        <div className="cht-absolute cht-top-3.5 cht-right-3.5">
          {item.raw.approval_status === "PENDING" ? (
            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-amber-400/90 cht-text-slate-900 cht-backdrop-blur-md cht-border cht-border-amber-300">
              Pending Review
            </span>
          ) : (
            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[9px] cht-font-black cht-bg-emerald-500/90 cht-text-white cht-backdrop-blur-md cht-flex cht-items-center cht-gap-1">
              <Check size={11} /> Approved
            </span>
          )}
        </div>

        {/* Hover Quick Actions on Bottom of Image */}
        <div className="cht-absolute cht-bottom-3 cht-left-3.5 cht-right-3.5 cht-flex cht-items-center cht-gap-2">
          <Button
            size="sm"
            variant="primary"
            className="cht-flex-1 cht-rounded-xl cht-shadow-md"
            leftIcon={<Edit3 size={13} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(item);
            }}
          >
            Manage Curriculum
          </Button>
          <Button
            size="sm"
            variant="outline"
            title="Preview Student View"
            className="cht-bg-white/90 cht-border-white/90 cht-text-slate-900 hover:cht-bg-white cht-rounded-xl cht-px-3"
            onClick={(e) => {
              e.stopPropagation();
              onPreview(item);
            }}
          >
            <ExternalLink size={13} />
          </Button>
        </div>
      </div>

      {/* Content Details */}
      <div className="cht-p-5 cht-space-y-4">
        <div>
          <h3 className="cht-text-base cht-font-bold cht-text-gray-900 cht-leading-snug cht-line-clamp-2 !cht-font-heading">
            {item.title}
          </h3>
          <div className="cht-flex cht-items-center cht-gap-3 cht-mt-2 cht-text-xs cht-text-slate-500">
            {item.duration && (
              <span className="cht-flex cht-items-center cht-gap-1">
                <Clock size={12} className="cht-text-[#3e57da]" />
                {item.duration}
              </span>
            )}
            <span className="cht-font-bold cht-text-slate-700">
              ₦{Number(item.online_price || 0).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Curriculum Stats Matrix */}
        <div className="cht-grid cht-grid-cols-3 cht-gap-2 cht-pt-3 cht-border-t cht-border-gray-100 cht-text-center">
          <div className="cht-p-2 cht-rounded-xl cht-bg-slate-50">
            <p className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">Modules</p>
            <p className="cht-text-sm cht-font-bold cht-text-slate-800 cht-mt-0.5">{item.modulesCount}</p>
          </div>
          <div className="cht-p-2 cht-rounded-xl cht-bg-slate-50">
            <p className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">Lessons</p>
            <p className="cht-text-sm cht-font-bold cht-text-slate-800 cht-mt-0.5">{item.lessonsCount}</p>
          </div>
          <div className="cht-p-2 cht-rounded-xl cht-bg-slate-50">
            <p className="cht-text-[10px] cht-font-black cht-text-slate-400 cht-uppercase cht-tracking-wider">Tasks</p>
            <p className="cht-text-sm cht-font-bold cht-text-slate-800 cht-mt-0.5">{item.tasksCount}</p>
          </div>
        </div>

        {item.type === "track" && item.coursesInTrack !== undefined && (
          <div className="cht-flex cht-items-center cht-gap-2 cht-pt-1 cht-text-[11px] cht-font-bold cht-text-[#3e57da]">
            <BookOpen size={13} />
            <span>Includes {item.coursesInTrack} linked courses</span>
          </div>
        )}
      </div>
    </GlassCard>
  </motion.div>
);

const InstructorCourses = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const { data: curriculum, isLoading: loading } = useInstructorCurriculumQuery();
  const courses = curriculum?.courses || [];
  const tracks = curriculum?.tracks || [];
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<"All" | "course" | "track">("All");

  // Transform into Unified Curriculum Items
  const unifiedItems: UnifiedCurriculumItem[] = useMemo(() => {
    const list: UnifiedCurriculumItem[] = [];

    // Map Courses
    courses.forEach((c) => {
      const mods = c.modules || [];
      const lessonsTotal = mods.reduce((acc, m) => acc + (m.lessons?.length || 0), 0) || (c.lessons_count || 0);
      const exercisesTotal = mods.reduce((acc, m) => acc + (m.exercise_module?.length || 0), 0);

      list.push({
        id: c.id || "",
        title: c.title,
        slug: c.slug || String(c.id),
        type: "course",
        status: "Published",
        level: c.level || "Beginner",
        duration: c.duration || "Self-Paced",
        online_price: c.online_price,
        onsite_price: c.onsite_price,
        thumbnail: c.course_img_url || "",
        modulesCount: mods.length,
        lessonsCount: lessonsTotal,
        tasksCount: exercisesTotal,
        studentsCount: c.enrolled_count || "0",
        raw: c,
      });
    });

    // Map Tracks
    tracks.forEach((t) => {
      const courseInTrack = t.course_in_track || [];
      const totalMods = courseInTrack.reduce((acc, c) => acc + (c.modules?.length || 0), 0);
      const totalLessons = courseInTrack.reduce((acc, c) => {
        return acc + (c.modules || []).reduce((mAcc, m) => mAcc + (m.lessons?.length || 0), 0);
      }, 0) || (t.lessons_count || 0);
      const totalExercises = courseInTrack.reduce((acc, c) => {
        return acc + (c.modules || []).reduce((mAcc, m) => mAcc + (m.exercise_module?.length || 0), 0);
      }, 0);

      list.push({
        id: t.id || "",
        title: t.title,
        slug: t.slug || String(t.id),
        type: "track",
        status: "Published",
        level: t.level || "Comprehensive",
        duration: t.duration || "Multi-Month",
        online_price: t.online_price,
        onsite_price: t.onsite_price,
        thumbnail: t.track_img_url || "",
        modulesCount: totalMods,
        lessonsCount: totalLessons,
        tasksCount: totalExercises,
        studentsCount: t.enrolled_count || "0",
        coursesInTrack: courseInTrack.length,
        raw: t,
      });
    });

    return list;
  }, [courses, tracks]);

  // Filter Items
  const filteredItems = useMemo(() => {
    return unifiedItems.filter((item) => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = selectedType === "All" || item.type === selectedType;
      return matchesSearch && matchesType;
    });
  }, [unifiedItems, searchQuery, selectedType]);

  // Actions
  const handleEdit = (item: UnifiedCurriculumItem) => {
    navigate(`/instructor/dashboard/my-courses/edit/${item.id}?type=${item.type}`);
  };

  const handlePreview = (item: UnifiedCurriculumItem) => {
    if (item.type === "track") {
      navigate(`/instructor/dashboard/my-tracks/${item.slug || item.id}/preview?trackId=${item.id}`);
    } else {
      navigate(`/instructor/dashboard/my-courses/${item.slug || item.id}/preview?courseId=${item.id}`);
    }
  };

  return (
    <div className="cht-space-y-8 cht-animate-in cht-fade-in cht-duration-500">
      {/* Header */}
      <header className="cht-flex cht-flex-col sm:cht-flex-row cht-items-start sm:cht-items-center cht-justify-between cht-gap-4">
        <div>
          <div className="cht-flex cht-items-center cht-gap-2.5">
            <h1 className="cht-text-3xl cht-font-black cht-text-[#1e293b] cht-tracking-tight !cht-font-heading">
              Faculty Curriculum Studio
            </h1>
            <span className="cht-px-2.5 cht-py-1 cht-rounded-full cht-text-[10px] cht-font-black cht-bg-blue-50 cht-text-[#3e57da] cht-border cht-border-blue-100">
              Assigned Courses Only
            </span>
          </div>
          <p className="cht-text-gray-500 cht-font-medium cht-text-sm cht-mt-1">
            Manage and author lesson content for the courses and tracks assigned to you by the SuperAdmin.
          </p>
        </div>
      </header>

      {/* Governance Banner */}
      <div className="cht-p-4 cht-rounded-2xl cht-bg-gradient-to-r cht-from-blue-50/80 cht-via-indigo-50/50 cht-to-slate-50 cht-border cht-border-blue-100/60 cht-flex cht-items-center cht-gap-3.5">
        <div className="cht-w-9 cht-h-9 cht-rounded-xl cht-bg-[#3e57da]/10 cht-text-[#3e57da] cht-flex cht-items-center cht-justify-center cht-shrink-0">
          <ShieldCheck size={20} />
        </div>
        <p className="cht-text-xs cht-text-slate-600 cht-leading-relaxed">
          <strong className="cht-text-slate-800">Faculty Content Governance:</strong> Course tuition pricing and track assignments are managed exclusively by the SuperAdmin. Any modules, lessons, or assignments you create, edit, or mark for deletion will be submitted for SuperAdmin approval before going live to students.
        </p>
      </div>

      {/* Metrics Row */}
      <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-3 cht-gap-5">
        <GlassCard className="cht-p-6 cht-rounded-[24px] cht-flex cht-items-center cht-gap-5">
          <div className="cht-w-12 cht-h-12 cht-bg-blue-50 cht-text-[#3e57da] cht-rounded-2xl cht-flex cht-items-center cht-justify-center">
            <BookOpen size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Assigned Courses</p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-0.5">{courses.length}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[24px] cht-flex cht-items-center cht-gap-5">
          <div className="cht-w-12 cht-h-12 cht-bg-purple-50 cht-text-purple-600 cht-rounded-2xl cht-flex cht-items-center cht-justify-center">
            <Layout size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Assigned Tracks</p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-0.5">{tracks.length}</h4>
          </div>
        </GlassCard>

        <GlassCard className="cht-p-6 cht-rounded-[24px] cht-flex cht-items-center cht-gap-5">
          <div className="cht-w-12 cht-h-12 cht-bg-emerald-50 cht-text-emerald-600 cht-rounded-2xl cht-flex cht-items-center cht-justify-center">
            <Layers size={22} />
          </div>
          <div>
            <p className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">Total Curricula</p>
            <h4 className="cht-text-2xl cht-font-black cht-text-gray-900 cht-mt-0.5">{unifiedItems.length}</h4>
          </div>
        </GlassCard>
      </div>

      {/* Filter and Search Bar */}
      <div className="cht-flex cht-flex-col sm:cht-flex-row cht-items-stretch sm:cht-items-center cht-gap-4 cht-bg-white cht-p-3.5 cht-rounded-[22px] cht-border cht-border-gray-100 cht-shadow-sm">
        {/* Type Tabs */}
        <div className="cht-flex cht-items-center cht-gap-1 cht-bg-slate-50 cht-p-1 cht-rounded-xl">
          {(["All", "course", "track"] as const).map((type) => (
            <button
              key={type}
              onClick={() => setSelectedType(type)}
              className={`cht-px-4 cht-py-2 cht-rounded-lg cht-text-xs cht-font-bold cht-capitalize cht-transition-all ${selectedType === type
                  ? "cht-bg-white cht-text-[#3e57da] cht-shadow-sm"
                  : "cht-text-slate-500 hover:cht-text-slate-800"
                }`}
            >
              {type === "All" ? "All Curricula" : type === "course" ? "Courses" : "Tracks"}
            </button>
          ))}
        </div>

        {/* Search Input */}
        <div className="cht-flex-1">
          <Input
            placeholder="Search assigned curricula by title..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            leftIcon={<Search size={16} />}
            containerClassName="cht-w-full"
            inputClassName="!cht-py-2 !cht-text-xs !cht-font-semibold"
          />
        </div>
      </div>

      {/* Course Grid */}
      {loading ? (
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-6">
          {[1, 2, 3].map((n) => (
            <div key={n} className="cht-h-80 cht-bg-slate-100 cht-animate-pulse cht-rounded-[28px]" />
          ))}
        </div>
      ) : filteredItems.length > 0 ? (
        <div className="cht-grid cht-grid-cols-1 md:cht-grid-cols-2 lg:cht-grid-cols-3 cht-gap-6">
          <AnimatePresence>
            {filteredItems.map((item) => (
              <CourseCard
                key={`${item.type}-${item.id}`}
                item={item}
                onEdit={handleEdit}
                onPreview={handlePreview}
              />
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <div className="cht-py-16 cht-text-center cht-bg-white cht-rounded-[32px] cht-border cht-border-gray-100">
          <div className="cht-w-16 cht-h-16 cht-bg-slate-50 cht-rounded-full cht-flex cht-items-center cht-justify-center cht-mx-auto cht-mb-4">
            <BookOpen size={28} className="cht-text-slate-300" />
          </div>
          <h3 className="cht-text-lg cht-font-black cht-text-slate-900 !cht-font-heading">
            {searchQuery ? "No matching curricula found" : "No courses or tracks assigned yet"}
          </h3>
          <p className="cht-text-slate-400 cht-text-xs cht-mt-1 cht-max-w-sm cht-mx-auto">
            {searchQuery
              ? "Try refining your search terms or clear your search."
              : "You have not been assigned to any course or career track yet. The SuperAdmin assigns faculty members to courses."}
          </p>
        </div>
      )}
    </div>
  );
};

export const DUMMY_COURSES: any[] = [];
export default InstructorCourses;
